<template>
  <VDialog v-model:visible="visible" :loading placement="right" :title="$t('invite-user')" width="40%" @submit="submit">
    <VForm :form>
      <VInput v-model="form.first_name" :label="$t('first-name')" :maxlength="100" prop="first_name" />
      <VInput v-model="form.last_name" :label="$t('last-name')" :maxlength="100" prop="last_name" />
      <VInput v-model="form.email" :label="$t('email')" :maxlength="255" prop="email" required>
        <template #below>
          <span v-if="emailWarnings.length" class="py-0 px-2" style="font-size: var(--body-x-small); color: var(--color-warning-500)">{{ emailWarnings.join(' ') }}</span>
        </template>
      </VInput>
      <VSelect v-if="$can('users.update')" v-model="form.group_ids" clearable filterable :label="$t('groups')" multiple>
        <VOption v-for="group in $groups" :key="group.id" :label="group.name" :value="group.id" />
      </VSelect>

      <SelectRoles v-if="$can('userRoles.update')" v-model="form.roles" clearable />
      <SelectModuleRoles v-if="$can('userRoles.update') && $organization.has_transaction_monitoring" v-model="form.module_roles" />
    </VForm>

    {{ $t('the-user-will-be-added-to-the-organisation-when-the-user-account-is-activated') }}
    <br />
    {{ $t('if-the-user-does-not-yet-have-a-pascal-account-the-password-will-be-randomly-generated') }} <br />
    {{ $t('the-new-user-will-have-to-enter-a-new-password-when-logging-in-for-the-first-time') }}
    <template #footer>
      <VButton :label="$t('cancel')" type="secondary" @click="visible = false" />
      <VButton :label="$t('apply')" :loading @click="submit" />
    </template>
  </VDialog>
</template>

<script setup lang="ts">
export type DialogUserCreateForm = ReturnType<typeof form.data>

interface Props {
  create: (user: DialogUserCreateForm) => void
}

const { create } = defineProps<Props>()

const visible = defineModel<boolean>('visible')
const emailWarnings = ref<string[]>([])
const form = useForm(
  {
    first_name: '',
    last_name: '',
    email: '',
    group_ids: [] as number[],
    roles: [] as string[],
    module_roles: [] as string[],
  },
  {
    rules: {
      first_name: { required: true },
      last_name: { required: true },
      email: {
        validator: async (email) => {
          const { errors, valid, warnings } = await validateEmail(email, { response_format: 'detailed' })

          if (Object.keys(warnings).length) {
            emailWarnings.value = Object.values(warnings).flat()
          } else {
            emailWarnings.value = []
          }

          return valid ? true : { message: Object.values(errors)[0][0] }
        },
      },
    },
  },
)

const loading = ref(false)

async function submit() {
  await $confirm({ title: $t('invite-user'), message: $t('if-you-proceed-with-this-action-you-will-invite-the-user-email-to-your-organisation', form) })
  loading.value = true

  try {
    await create(form.data())
    $message.success($t('the-user-first_name-last_name-has-been-invited-in-an-email-sent-to-email', form))
    visible.value = false
  } catch {
    $message.error($t('api-error'))
  } finally {
    loading.value = false
  }
}
</script>
